import { useState } from "react";
import { toast } from "sonner";
import {
  Archive,
  Download,
  Link2,
  RotateCcw,
  CalendarDays,
  Loader2,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { callSheetApi } from "@/lib/api";

function fmtDate(iso) {
  if (!iso) return "Unscheduled";
  try {
    const [y, m, d] = iso.split("-").map(Number);
    return new Date(y, (m || 1) - 1, d || 1).toLocaleDateString(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

/**
 * Modal listing archived (wrapped) call-sheet days for a project.
 *
 * Each row exposes:
 *  - Download PDF (still works — the day lives on in the DB)
 *  - Copy public share link
 *  - Restore (unarchive) — admin only
 */
export function PastCallSheetsDialog({
  open,
  onOpenChange,
  projectId,
  archivedDays,
  canEdit,
  onRestored,
}) {
  const [restoringId, setRestoringId] = useState(null);

  const handleCopyShare = async (dayId) => {
    const url = `${window.location.origin}/share/call-sheet/${dayId}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Share link copied");
    } catch {
      window.prompt("Copy this link:", url);
    }
  };

  const handleRestore = async (dayId) => {
    setRestoringId(dayId);
    try {
      await callSheetApi.unarchiveDay(projectId, dayId);
      toast.success("Shoot day restored");
      onRestored?.(dayId);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to restore");
    } finally {
      setRestoringId(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="past-call-sheets-dialog"
        className="bg-zinc-950 border-zinc-800 text-zinc-100 sm:max-w-2xl"
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl font-light flex items-center gap-2">
            <Archive className="w-5 h-5 text-amber-500" />
            Past Call Sheets
          </DialogTitle>
          <DialogDescription className="text-zinc-500">
            Archived shoot days for this project. Their PDFs and share links
            keep working — you can also restore any day back to the active
            list.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-2 max-h-[60vh] overflow-y-auto">
          {archivedDays.length === 0 ? (
            <div className="border border-dashed border-zinc-800 rounded-md py-10 text-center text-zinc-500 text-sm">
              No wrapped shoots yet.
            </div>
          ) : (
            <div className="border border-zinc-800 rounded-md divide-y divide-zinc-800/70">
              {archivedDays.map((d) => {
                const label = d.day_label?.trim() || `Day ${d.day_number}`;
                return (
                  <div
                    key={d.id}
                    data-testid={`past-call-sheet-row-${d.day_number}`}
                    className="flex items-center gap-3 px-4 py-3 hover:bg-zinc-900/40 transition-colors"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <div className="text-overline text-zinc-200">{label}</div>
                        <span className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-emerald-500/10 text-emerald-400 border border-emerald-500/30">
                          Wrapped
                        </span>
                      </div>
                      <div className="text-xs text-zinc-500 mt-0.5 flex items-center gap-1.5">
                        <CalendarDays className="w-3 h-3" />
                        {fmtDate(d.shoot_date)}
                        {d.day_of ? (
                          <span className="text-zinc-600">· {d.day_of}</span>
                        ) : null}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <a
                        data-testid={`past-day-pdf-${d.day_number}`}
                        href={callSheetApi.pdfUrlForDay(projectId, d.id)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-zinc-300 hover:text-amber-500 hover:bg-amber-500/10 rounded-sm transition-colors"
                        title="Download PDF"
                      >
                        <Download className="w-3.5 h-3.5" /> PDF
                      </a>
                      <button
                        data-testid={`past-day-share-${d.day_number}`}
                        onClick={() => handleCopyShare(d.id)}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-zinc-300 hover:text-amber-500 hover:bg-amber-500/10 rounded-sm transition-colors"
                        title="Copy share link"
                      >
                        <Link2 className="w-3.5 h-3.5" /> Share
                      </button>
                      {canEdit && (
                        <Button
                          data-testid={`past-day-restore-${d.day_number}`}
                          variant="ghost"
                          size="sm"
                          disabled={restoringId === d.id}
                          onClick={() => handleRestore(d.id)}
                          className="text-zinc-400 hover:text-amber-500 hover:bg-amber-500/10 h-auto py-1.5 px-2.5 text-xs"
                        >
                          {restoringId === d.id ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <>
                              <RotateCcw className="w-3.5 h-3.5 mr-1" /> Restore
                            </>
                          )}
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
